import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  CircularProgress,
  Chip,
  Alert,
  Divider,
  Box,
  Switch,
  FormControlLabel,
  Paper,
  InputAdornment,
  MenuItem
} from '@mui/material';
import { CurrencyContext } from '../contexts/CurrencyContext';
import { getProductsByOrgId } from '../services/productService';
import { OrgContext } from '../contexts/OrgContext';
import { getLookupValues, createLookupValue } from '../services/lookupService';

const currencySymbols = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'CHF',
  CNY: '¥',
  INR: '₹',
  JPY: '¥',
  BDT: '৳',
};

const ReturnExchangeModal = ({ open, onClose, order, onSubmit, returnHistory = [], mode = 'return' }) => {
  const [returnItems, setReturnItems] = useState([]);
  const [exchangeItems, setExchangeItems] = useState([]);
  const [returnReason, setReturnReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');
  const [returnReasonOptions, setReturnReasonOptions] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [availableItems, setAvailableItems] = useState([]); // {product, available, base, product_id}

  const { currency } = useContext(CurrencyContext);
  const { orgId } = useContext(OrgContext);
  const symbol = currencySymbols[currency] || currency;

  const loadProducts = useCallback(async () => {
    try {
      const productsData = await getProductsByOrgId(orgId);
      setProducts(productsData || []);
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Failed to load products for exchange');
    }
  }, [orgId]);

  const fetchReturnReasons = useCallback(async () => {
    setLoadingReasons(true);
    try {
      const reasons = await getLookupValues(orgId, 'return_reason');
      setReturnReasonOptions(reasons);
    } catch (error) {
      console.error('Failed to fetch return reasons:', error);
    } finally {
      setLoadingReasons(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (order?.lineItems && open) {
      const initialReturnItems = order.lineItems.map(item => ({
        ...item,
        return_quantity: 0,
        max_returnable: item.quantity - (item.returned_quantity || 0),
        original_line_item_id: item.id
      }));
      setReturnItems(initialReturnItems);
      
      loadProducts();
    }
  }, [order, open, loadProducts]);

  useEffect(() => {
    if (open && orgId) {
      fetchReturnReasons();
    }
  }, [open, orgId, fetchReturnReasons]);

  // Build availability from order + history
  useEffect(() => {
    if (!order || !open) return;
    const base = new Map(); // product_id -> { product, qty }
    order.lineItems.forEach(li => {
      base.set(li.product.id, {
        product: li.product,
        baseQty: li.quantity,
        exchangedIn: 0,
        returned: 0,
      });
    });

    returnHistory.forEach(h => {
      h.lineItems.forEach(li => {
        const pid = li.product.id;
        if (!base.has(pid)) {
          base.set(pid, {
            product: li.product,
            baseQty: 0,
            exchangedIn: 0,
            returned: 0,
          });
        }
        const rec = base.get(pid);
        if (h.order_type === 'Return') {
          rec.returned += li.quantity; // stored positive
        } else if (h.order_type === 'Exchange') {
          if (li.quantity < 0) {
            rec.returned += Math.abs(li.quantity);
          } else if (li.quantity > 0) {
            rec.exchangedIn += li.quantity;
          }
        }
      });
    });

    const list = [];
    base.forEach((v, pid) => {
      const available = v.baseQty + v.exchangedIn - v.returned;
      if (available > 0) {
        list.push({
          product_id: pid,
            product: v.product,
            original_qty: v.baseQty,
            exchanged_in: v.exchangedIn,
            returned_qty: v.returned,
            available,
        });
      }
    });
    setAvailableItems(list);
  }, [order, returnHistory, open]);

  // Build returnItems whenever availability recalculated
  useEffect(() => {
    if (!open) return;
    const mapped = availableItems.map(ai => ({
      product_id: ai.product_id,                // added stable id
      product: { product_name: ai.product.product_name, id: ai.product.id },
      quantity: ai.original_qty + ai.exchanged_in,
      return_quantity: 0,
      max_returnable: ai.available,
      original_line_item_id: order.lineItems.find(li => li.product.id === ai.product_id)?.id ?? null,
      unit_price:
        (order.lineItems.find(li => li.product.id === ai.product_id)?.unit_price) ||
        ai.product.current_unit_price ||
        ai.product.current_unit_cost ||
        0,
      discount_type: 'none',
      discount_value: 0,
    }));
    setReturnItems(mapped);
    setExchangeItems([]); // reset exchange draft when availability refreshes
  }, [availableItems, open, order]);

  const handleReturnQuantityChange = (productId, quantity) => {
    setReturnItems(prev =>
      prev.map(item =>
        item.product_id === productId
          ? { ...item, return_quantity: quantity }
          : item
      )
    );
  };

  const handleAddExchangeItem = () => {
    setExchangeItems(prev => [...prev, {
      id: Date.now(),
      product_id: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0
    }]);
  };

  const handleRemoveExchangeItem = (id) => {
    setExchangeItems(prev => prev.filter(item => item.id !== id));
  };

  const handleExchangeItemChange = (id, field, value) => {
    setExchangeItems(prev => prev.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        
        if (field === 'quantity' || field === 'unit_price') {
          updatedItem.total_price = updatedItem.quantity * updatedItem.unit_price;
        }
        
        if (field === 'product_id') {
          const selectedProduct = products.find(p => p.id === value);
          if (selectedProduct) {
            updatedItem.unit_price = order.order_type === 'Sale' 
              ? selectedProduct.current_unit_price 
              : selectedProduct.current_unit_cost;
            updatedItem.total_price = updatedItem.quantity * updatedItem.unit_price;
          }
        }
        
        return updatedItem;
      }
      return item;
    }));
  };

  const calculateReturnAmount = () => {
    return returnItems.reduce((sum, item) => 
      sum + (item.return_quantity * item.unit_price), 0
    );
  };

  const calculateExchangeAmount = () => {
    return exchangeItems.reduce((sum, item) => 
      sum + item.total_price, 0
    );
  };

  const calculateNetAmount = () => {
    return calculateExchangeAmount() - calculateReturnAmount();
  };

  const getReturnableItems = () => {
    return returnItems.filter(item => item.return_quantity > 0);
  };

  const validateForm = () => {
    const returnableItems = getReturnableItems();
    
    if (returnableItems.length === 0) {
      setError('Please select at least one item to return');
      return false;
    }

    if (mode === 'exchange' && exchangeItems.length === 0) {
      setError('Please add at least one item to exchange');
      return false;
    }

    if (mode === 'exchange') {
      for (const item of exchangeItems) {
        if (!item.product_id || item.quantity <= 0 || item.unit_price <= 0) {
          setError('Please complete all exchange item details');
          return false;
        }
      }
    }

    // Return reason is now optional - no validation needed
    setError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Only save return reason to lookup table if it's provided and not empty
      if (returnReason && returnReason.trim() !== '') {
        // Check if the reason already exists in the options
        const reasonExists = returnReasonOptions.some(
          option => option.toLowerCase() === returnReason.toLowerCase()
        );
        
        if (!reasonExists) {
          try {
            console.log('Saving new return reason:', returnReason);
            await createLookupValue(orgId, 'return_reason', returnReason.trim());
            console.log('Successfully saved new return reason');
            
            // Update the local options to include the new reason
            setReturnReasonOptions(prev => [...prev, returnReason.trim()]);
          } catch (error) {
            // Fail silently - the return still proceeds even if saving the reason fails
            console.error('Failed to save new return reason:', error);
          }
        }
      }

      const returnableItems = getReturnableItems();
      const returnData = {
        returned_items: returnableItems.map(item => ({
          product_id: item.product_id || item.product.id, // prefer product_id
          quantity: item.return_quantity,
          unit_price: parseFloat(item.unit_price),
          total_price: item.return_quantity * parseFloat(item.unit_price),
          original_unit_price: parseFloat(item.original_unit_price || item.unit_price),
          discount_type: item.discount_type,
          discount_value: item.discount_value,
          ...(item.original_line_item_id ? { original_line_item_id: item.original_line_item_id } : {})
        })),
        return_reason: returnReason || null,
        meta: {},
        org_id: order.organization?.id
      };
      
      if (mode === 'exchange') {
        const exchangeData = {
          ...returnData,
          exchanged_items: exchangeItems.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            unit_price: item.unit_price,
            total_price: item.total_price,
            original_unit_price: item.unit_price,
            discount_type: 'none',
            discount_value: null
          }))
        };
        await onSubmit('exchange', exchangeData);
      } else {
        await onSubmit('return', returnData);
      }

      handleClose();
    } catch (error) {
      setError(error.message || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReturnItems([]);
    setExchangeItems([]);
    setReturnReason('');
    setError('');
    onClose();
  };

  if (!order) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {mode === 'exchange' ? 'Exchange Items' : 'Return Items'} - Order #{order.id}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Summary Chips Row */}
        {/*
          Added netColor so Selected Return Qty & Return Amount share
          the same background/text styling as the Net chip.
        */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {(() => {
            const netColor =
              mode === 'exchange'
                ? (calculateNetAmount() === 0
                    ? 'default'
                    : (calculateNetAmount() > 0 ? 'success' : 'error'))
                : 'success';
            return (
              <>
                <Chip
                  label={`Selected Return Qty: ${
                    returnItems.reduce((s,i)=>s + (i.return_quantity||0),0)
                  }`}
                  color={netColor}
                  variant="filled"
                />
                <Chip
                  label={`Return Amount: ${symbol}${calculateReturnAmount().toFixed(2)}`}
                  color={netColor}
                  variant="filled"
                />
                {mode === 'exchange' && (
                  <>
                    <Chip
                      label={`Exchange Amount: ${symbol}${calculateExchangeAmount().toFixed(2)}`}
                      color="warning"
                      variant="outlined"
                    />
                    <Chip
                      label={`Net: ${symbol}${calculateNetAmount().toFixed(2)}`}
                      color={netColor}
                      variant="filled"
                    />
                  </>
                )}
              </>
            );
          })()}
        </Box>

        {/* Return Items Section */}
        <Typography variant="h6" gutterBottom>
          Select Items to Return
        </Typography>
        
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Acquired Qty</TableCell>
                <TableCell>Available to Return</TableCell>
                <TableCell>Return Qty</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Return Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {returnItems.map(item => {
                const unit = parseFloat(item.unit_price || 0);
                return (
                  <TableRow key={item.product_id}>
                    <TableCell>{item.product?.product_name}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.max_returnable}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.return_quantity}
                        onChange={(e) => {
                          const raw = parseInt(e.target.value, 10);
                          const clamped = isNaN(raw)
                            ? 0
                            : Math.min(item.max_returnable, Math.max(0, raw));
                          handleReturnQuantityChange(item.product_id, clamped);
                        }}
                        inputProps={{
                          min: 0,
                          max: item.max_returnable,
                          inputMode: 'numeric',
                          pattern: '[0-9]*'
                        }}
                        sx={{
                          width: 80,
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0
                          },
                          '& input[type=number]': {
                            MozAppearance: 'textfield'
                          }
                        }}
                        autoComplete="off"
                      />
                    </TableCell>
                    <TableCell>{symbol}{unit.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${symbol}${(item.return_quantity * unit).toFixed(2)}`}
                        color={item.return_quantity > 0 ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Exchange Items Section */}
        {mode === 'exchange' && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={1}>
              <Typography variant="h6">
                Exchange Items
              </Typography>
              <Button
                variant="contained"
                color="warning"
                onClick={handleAddExchangeItem}
                sx={{
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#f57c00' }
                }}
              >
                Add Item
              </Button>
            </Box>

            {exchangeItems.map((item, index) => (
              <Paper key={item.id} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="Product"
                      value={item.product_id}
                      onChange={(e) => handleExchangeItemChange(item.id, 'product_id', e.target.value)}
                      size="small"
                    >
                      {products.map((product) => (
                        <MenuItem key={product.id} value={product.id}>
                          {product.product_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      type="number"
                      label="Quantity"
                      value={item.quantity}
                      onChange={(e) => handleExchangeItemChange(item.id, 'quantity', parseInt(e.target.value) || 0)}
                      inputProps={{ min: 1 }}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      type="number"
                      label="Unit Price"
                      value={item.unit_price}
                      onChange={(e) => handleExchangeItemChange(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
                      }}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Total"
                      value={item.total_price.toFixed(2)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
                        readOnly: true,
                      }}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleRemoveExchangeItem(item.id)}
                      size="small"
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </>
        )}

        {/* Return Reason - now optional */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Autocomplete
            value={returnReason}
            onChange={(event, newValue) => {
              setReturnReason(newValue || '');
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
              // Also update returnReason when typing
              setReturnReason(newInputValue);
            }}
            options={returnReasonOptions}
            freeSolo
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label="Return Reason (Optional)"
                variant="outlined"
                placeholder="Enter or select a reason (optional)"
                // Remove required and error props since it's now optional
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingReasons ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Summary */}
        <Divider sx={{ my: 2 }} />
        <Box>
          <Typography variant="h6" gutterBottom>Summary</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography>Return Amount: {symbol}{calculateReturnAmount().toFixed(2)}</Typography>
            </Grid>
            {mode === 'exchange' && (
              <>
                <Grid item xs={6}>
                  <Typography>Exchange Amount: {symbol}{calculateExchangeAmount().toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" color={calculateNetAmount() >= 0 ? 'success.main' : 'error.main'}>
                    Net Amount: {symbol}{calculateNetAmount().toFixed(2)}
                    {calculateNetAmount() > 0 && ' (Additional payment required)'}
                    {calculateNetAmount() < 0 && ' (Refund due)'}
                    {calculateNetAmount() === 0 && ' (Even exchange)'}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || getReturnableItems().length === 0}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Processing...' : (mode === 'exchange' ? 'Process Exchange' : 'Process Return')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReturnExchangeModal;
